import { useEffect, useState } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { EyeIcon, PlusIcon, XCircleIcon } from "@heroicons/react/24/outline";

import { Button } from "../../components/base/Button";
import { Table } from "../../components/base/Table/Table";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useNotification from "../../components/shared/Notifications/useNotifications";
import useCoreDeploymentMutations from "../../data/CoreDeployment/useCoreDeploymentMutations";
import { CoreDeploymentQueryKeys } from "../../data/CoreDeployment/keys";
import { ICoreDeploymentRun, canCancelRun } from "../../models/CoreDeployment/CoreDeploymentRun";
import { ScheduleCoreDeploymentModal } from "../../components/CoreDeployments/ScheduleCoreDeploymentModal";
import { ViewCoreDeploymentModal } from "../../components/CoreDeployments/ViewCoreDeploymentModal";
import {
  CoreDeploymentStatusPill,
  CoreTargetBranchPill,
  formatDateTime,
} from "../../components/CoreDeployments/coreDeploymentMeta";

const colHelper = createColumnHelper<ICoreDeploymentRun>();

const REFRESH_MS = 30000;

export default function CoreDeploymentsPage() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { cancel } = useCoreDeploymentMutations();
  const queryClient = useQueryClient();

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState<ICoreDeploymentRun | null>(null);

  useEffect(() => {
    setInfo({
      name: "Core Deployments",
      desc: "Schedule core-staging → core-deployment / tme-deployment merges",
      pageTitle: "Core Deployments",
    });
  }, []);

  // The worker moves runs along once a minute; keep the list fresh without a reload.
  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: CoreDeploymentQueryKeys.all() });
    }, REFRESH_MS);
    return () => clearInterval(interval);
  }, [queryClient]);

  const handleView = (run: ICoreDeploymentRun) => {
    setViewingId(run.id);
    setViewOpen(true);
  };

  const handleCancel = (run: ICoreDeploymentRun) => {
    setCancelling(run);
    setCancelOpen(true);
  };

  const handleConfirmCancel = () => {
    if (!cancelling) return;

    cancel.mutate(cancelling.id, {
      onSuccess: () => {
        addNotification({ variant: "success", primaryText: "Deployment cancelled" });
        setCancelOpen(false);
        setCancelling(null);
      },
      onError: (error: Error) => {
        addNotification({
          variant: "error",
          primaryText: error?.message || "Failed to cancel deployment",
        });
        queryClient.invalidateQueries({ queryKey: CoreDeploymentQueryKeys.all() });
      },
    });
  };

  const cols = [
    colHelper.accessor("scheduledFor", {
      header: "Scheduled for",
      cell: (info) => formatDateTime(info.getValue()),
    }),
    colHelper.display({
      id: "targets",
      header: "Targets",
      cell: (info) => {
        const run = info.row.original;
        return (
          <div className="flex gap-1">
            {run.deployCore && <CoreTargetBranchPill branch="core-deployment" />}
            {run.deployTme && <CoreTargetBranchPill branch="tme-deployment" />}
          </div>
        );
      },
    }),
    colHelper.accessor("status", {
      header: "Status",
      cell: (info) => <CoreDeploymentStatusPill status={info.getValue()} />,
    }),
    colHelper.accessor("requestedByEmail", {
      header: "Requested by",
      cell: (info) => info.row.original.requestedByName || info.getValue(),
    }),
    colHelper.accessor("completedAt", {
      header: "Completed",
      cell: (info) => formatDateTime(info.getValue()),
    }),
    colHelper.accessor("failureReason", {
      header: "Result",
      cell: (info) => {
        const run = info.row.original;
        const text = info.getValue();
        if (run.status === "Cancelled") return <span className="text-gray-500">Cancelled by {run.cancelledByEmail ?? "-"}</span>;
        if (!text) return <span className="text-gray-400">-</span>;
        return (
          <span className="block max-w-md truncate text-red-700" title={text}>
            {text}
          </span>
        );
      },
    }),
    colHelper.display({
      id: "actions",
      header: "Actions",
      cell: (info) => {
        const run = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-1 rounded hover:text-blue-700"
              onClick={() => handleView(run)}
              title="View deployment"
            >
              <EyeIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
            </button>
            {canCancelRun(run.status) && (
              <button
                type="button"
                className="p-1 rounded hover:text-red-700"
                onClick={() => handleCancel(run)}
                disabled={cancel.isPending}
                title="Cancel deployment"
              >
                <XCircleIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
              </button>
            )}
          </div>
        );
      },
    }),
  ];

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <ScheduleCoreDeploymentModal open={scheduleOpen} setOpen={setScheduleOpen} />

      <ViewCoreDeploymentModal open={viewOpen} setOpen={setViewOpen} runId={viewingId} />

      <ConfirmModal
        open={cancelOpen}
        setOpen={(open: boolean) => {
          setCancelOpen(open);
          if (!open) setCancelling(null);
        }}
        title={"Cancel Deployment?"}
        message={`Cancel the deployment scheduled for ${formatDateTime(cancelling?.scheduledFor)}?\nNothing will be merged.`}
        confirmButtonText={"Cancel deployment"}
        onConfirm={handleConfirmCancel}
        isLoading={cancel.isPending}
      />

      <div className="flex justify-end">
        <Button onClick={() => setScheduleOpen(true)} size="sm" className="flex items-center space-x-2">
          <PlusIcon className="h-5 w-5 -ml-1" />
          <p>Schedule Deployment</p>
        </Button>
      </div>

      <Table<ICoreDeploymentRun>
        url="/core-deployments"
        queryKey={CoreDeploymentQueryKeys.all()}
        columns={cols}
        searchLabel="Search by requester, status or notes"
      />
    </div>
  );
}
